import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Injector,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { SmsService } from '../sms.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { SmsModel } from '@fi-app/sms/sms.model';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IItemUpdatedEventData } from '@fi-shared/fi-signal-r/fi-signal-r';
import { ActionType } from '@fi-shared/types';
import { IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { takeUntil } from 'rxjs/operators';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { IContactModel } from '@fi-shared/models/common';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';
import { FiContactInformationTypes } from '@fi-shared/types/fi-contact-information-types';

@Component({
    selector: 'fi-sms-list',
    templateUrl: './sms-list.component.html',
    styleUrls: ['./sms-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-sms-list' },
    standalone: false,
})
export class SmsListComponent extends FiRemoteStateFullDataSource<SmsModel> implements OnInit, OnDestroy {
    public defaultSortColumnName = 'CreatedAt';
    public defaultSortDirection = true;
    public objectType = ObjectTypes.SMSMessage;
    public itemsListType = FiItemsListType.SMS;

    private smsService = inject(SmsService);
    private fiSignalRHelperService = inject(FiSignalRHelperService);
    private fiContactsService = inject(FiContactsService);

    constructor(injector: Injector) {
        super(injector);
    }

    @HostBinding('class.mobile-view') get isMobileView(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    public ngOnInit(): void {
        this.fiSignalRHelperService.itemUpdated$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((data) => {
            this.onDbOperation(data);
        });
    }

    private onDbOperation(data: IItemUpdatedEventData): void {
        if (data.ObjectType === ObjectTypes.SMSMessage && data.Action === ActionType.Update) {
            const items = this.items();
            const currentSms = items.find((item) => item.Id === data.Id);
            if (currentSms) {
                const params: IItemsListParameterBase = {
                    Ids: [currentSms.Id],
                };
                this.mergeLocalSearchParameter(params);
                this.smsService.getSMSList(params).then((items) => {
                    this.removeData([currentSms]);
                    this.appendData(items, {
                        appendToTop: true,
                    });
                });
            }
        }
    }

    public loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        return this.smsService.getSMSListData(parameter);
    }

    protected getToText(item: { Contacts: IContactModel[] }): string {
        return item.Contacts.map((contact) => {
            let result: string = this.fiContactsService.getFullContactValueByProps(
                contact.ContactType ?? FiContactInformationTypes.WorkPhoneNumber,
                contact.ContactValue,
                contact.ExtendedValue,
            );
            /*if (contact.UserName) {
                result += ' ' + contact.UserName;
            }
            if (contact.ContactType) {
                result += ' (' + this.contactService.getPhoneTypeName(contact.ContactType) + ')';
            }*/
            return result;
        }).join(', ');
    }

    protected getDeliveredText(data: SmsModel): string {
        let delivered = 0;
        data.Contacts.forEach(function (contact) {
            if (contact.SentAt && !contact.SentErrors) {
                delivered++;
            }
        });
        return delivered + ' / ' + data.Contacts.length;
    }
}
